import {
  getDatabase,
  ref,
  child,
  set,
  serverTimestamp,
  get,
} from 'firebase/database';
import RealtimeDatabaseTransport from '@codefluegel/firebase-realtime-db-transport/web';
import firebaseApp from './app';
import {
  FireSafetyEntry,
  MaexchenCollectCredit,
  MaexchenRedeemCredit,
  MaexchenUserData,
  Message,
  News,
  Video,
  VideoCategory,
} from '../types/transport';
import {
  Contact,
  Course,
  Document,
  FireSafety,
  ProductFinderData,
  ProductFinderConfig,
  ProductOrMaterialByKey,
  Seller,
  MaexchenRedeemProducts,
  MaexchenCollectProducts,
} from '../csvImport';

const dbPromise = firebaseApp.then(app => getDatabase(app));

export const userTransport = new RealtimeDatabaseTransport<MaexchenUserData>(
  dbPromise.then(database => ref(database, 'user/maexchen')),
);

export const newsTransport = new RealtimeDatabaseTransport<News>(
  dbPromise.then(database => ref(database, 'news/role')),
);

export const videoCagegoryTransport =
  new RealtimeDatabaseTransport<VideoCategory>(
    dbPromise.then(database => ref(database, 'video/category')),
  );

export const videoTransport = new RealtimeDatabaseTransport<Video>(
  dbPromise.then(database => ref(database, 'video/video')),
);

export const fireSafetyTransport =
  new RealtimeDatabaseTransport<FireSafetyEntry>(
    dbPromise.then(database => ref(database, 'fireSafetyCategory')),
  );

export const redeemProductsTransport =
  new RealtimeDatabaseTransport<MaexchenRedeemProducts>(
    dbPromise.then(database => ref(database, 'maexchenGame/redeemProducts')),
  );

export const configTransport = new RealtimeDatabaseTransport<string | number>(
  dbPromise.then(database => ref(database, 'config')),
);

export const updateProducts = async (
  productData: ProductOrMaterialByKey,
): Promise<void> => set(ref(await dbPromise, 'product'), productData);

export const updateDocuments = async (
  documentData: Record<string, Document>,
): Promise<void> => set(ref(await dbPromise, 'document'), documentData);

export const updateFireSafety = async (
  fireSafetyData: Record<string, FireSafety>,
): Promise<void> => set(ref(await dbPromise, 'fireSafety'), fireSafetyData);

export const updateSellers = async (
  sellerData: Record<string, Seller>,
): Promise<void> => set(ref(await dbPromise, 'seller'), sellerData);

export const updateCourses = async (
  courseData: Record<string, Course>,
): Promise<void> => set(ref(await dbPromise, 'course'), courseData);

export const updateContacts = async (
  contactData: Record<string, Contact>,
): Promise<void> => set(ref(await dbPromise, 'contact'), contactData);

export const updateProductFinder = async (productfinderData: {
  config: Record<string, ProductFinderConfig>;
  data: Record<string, ProductFinderData>;
}): Promise<void> =>
  set(ref(await dbPromise, 'productFinder'), productfinderData);

export const addMessageEntry = async (notification: Message): Promise<void> =>
  set(child(ref(await dbPromise, 'message'), String(new Date().getTime())), {
    ...notification,
    createdAt: serverTimestamp(),
  });

export const updateMaexchenProductCatalog = async (
  maexchenProductData: Record<string, MaexchenRedeemProducts>,
): Promise<void> =>
  set(ref(await dbPromise, 'maexchenGame/redeemProducts'), maexchenProductData);

export const updateMaexchenCollectProducts = async (
  maexchenCollectProductData: Record<string, MaexchenCollectProducts>,
): Promise<void> =>
  set(
    ref(await dbPromise, 'maexchenGame/collectProducts'),
    maexchenCollectProductData,
  );

export const isFrontendAdmin = async (userId: string): Promise<boolean> => {
  const reference = child(
    child(ref(await dbPromise, '/user/adminPage'), userId),
    'isAdmin',
  );
  return !!(await get(reference)).val();
};

/**
   @param ownUserId frontend user if (of the currently logged in FE
   user), e.g. retrieved from `AuthStore`
   @param pointsGroupId group id where the points should be added
   @param credits number of maexchen to be added
 */
export const addMaexchenCollectProductToGroup = async (
  ownUserId: string,
  pointsGroupId: string,
  credits: number,
  productDescription: string,
): Promise<void> => {
  const refToStore = child(
    child(
      child(ref(await dbPromise, 'maexchenGame/byUserGroup'), pointsGroupId),
      'collectedCredits',
    ),
    new Date().valueOf().toString(),
  );
  return set(refToStore, {
    addedExternally: true,
    credits,
    timestamp: serverTimestamp(),
    userId: ownUserId,
    productDescription,
  });
};

// one-shot read of everything the maexchen export needs, instead of opening
// a live listener per user group
export const getMaexchenExportData = async (): Promise<{
  users: Record<string, MaexchenUserData>;
  byUserGroup: Record<
    string,
    {
      collectedCredits?: Record<string, MaexchenCollectCredit>;
      redeemedCredits?: Record<string, MaexchenRedeemCredit>;
    }
  >;
  redeemProducts: Record<string, MaexchenRedeemProducts>;
}> => {
  const database = await dbPromise;
  const [users, byUserGroup, redeemProducts] = await Promise.all([
    get(ref(database, 'user/maexchen')),
    get(ref(database, 'maexchenGame/byUserGroup')),
    get(ref(database, 'maexchenGame/redeemProducts')),
  ]);
  return {
    users: users.val() ?? {},
    byUserGroup: byUserGroup.val() ?? {},
    redeemProducts: redeemProducts.val() ?? {},
  };
};

export const collectedPointsTransportFactory = (
  groupId: string,
): RealtimeDatabaseTransport<MaexchenCollectCredit> => {
  const queryRef = dbPromise.then(database =>
    ref(database, `maexchenGame/byUserGroup/${groupId}/collectedCredits`),
  );
  return new RealtimeDatabaseTransport<MaexchenCollectCredit>(queryRef);
};

export const redeemedPointsTransportFactory = (
  groupId: string,
): RealtimeDatabaseTransport<MaexchenRedeemCredit> => {
  const queryRef = dbPromise.then(database =>
    ref(database, `maexchenGame/byUserGroup/${groupId}/redeemedCredits`),
  );
  return new RealtimeDatabaseTransport<MaexchenRedeemCredit>(queryRef);
};
